<template>
  <div v-if="summaries.length" class="fan-automation-panel space-y-3">
    <div>
      <h4 class="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
        {{ tt('Fan Automations', 'Avtomatizacije ventilatorja') }}
      </h4>
      <p class="text-xs text-gray-500 dark:text-gray-400">
        {{
          tt(
            'Temperature / humidity controller and presence timer',
            'Krmilnik temperature / vlage in časovnik prisotnosti',
          )
        }}
      </p>
    </div>

    <article
      v-for="automation in summaries"
      :key="automation.id"
      class="rounded-3xl border border-sky-200 bg-sky-50/40 p-4 shadow-sm dark:border-sky-900/50 dark:bg-sky-500/5">
      <!-- Header: status badge + name/status + main value -->
      <div class="flex items-center gap-3">
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors"
          :class="
            isActive(automation)
              ? 'bg-sky-500/20 text-sky-500'
              : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
          ">
          <ControlIcon :name="automationIcon(automation)" size="1.25rem" />
        </div>

        <div class="min-w-0 flex-1">
          <h5 class="truncate text-base font-semibold text-gray-900 dark:text-white">
            {{ automation.name }}
          </h5>
          <p class="truncate text-sm text-gray-600 dark:text-gray-300">
            {{ statusLabel(automation) }}
          </p>
        </div>

        <div class="shrink-0 rounded-2xl bg-sky-100/70 px-3 py-2 text-right dark:bg-sky-500/10">
          <div class="text-lg font-semibold leading-none text-sky-600 dark:text-sky-300">
            {{ mainValueLabel(automation) }}
          </div>
          <div class="mt-1 text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {{ mainValueCaption(automation) }}
          </div>
        </div>
      </div>

      <!-- Detail line -->
      <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
        <template v-if="automation.automationType === FAN_TEMP_CONTROLLER_TYPE">
          <span>{{ tt('Sensor:', 'Senzor:') }} {{ sensorLabel(automation) }}</span>
          <span>{{ tt('Mode:', 'Način:') }} {{ controlModeLabel(automation) }}</span>
          <span v-if="sensorValue(automation) != null"
            >{{ tt('Value:', 'Vrednost:') }} {{ sensorValue(automation) }}</span
          >
        </template>
        <template v-else-if="automation.automationType === FAN_PRESENCE_TIMER_TYPE">
          <span>{{ tt('Presence:', 'Prisotnost:') }} {{ presenceLabel(automation) }}</span>
          <span v-if="timerActive(automation)"
            >{{ tt('Countdown:', 'Odštevanje:') }} {{ countdownLabel(automation) }}</span
          >
        </template>
        <template v-else>
          <span>{{ tt('Mode:', 'Način:') }} {{ switchModeLabel(automation) }}</span>
          <span v-if="externalLastAction(automation)"
            >{{ tt('Last:', 'Zadnje:') }} {{ externalLastAction(automation) }}</span
          >
        </template>
        <span
          v-if="automation.enabled === false"
          class="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          {{ tt('Disabled', 'Onemogočeno') }}
        </span>
      </div>

      <!-- Full settings — CONFIGURATION, so the editor and its Save button are
           the admin's alone (cards t_bd611e14 / t_a4368691; D12: settings
           hidden, control shown). A non-admin keeps the status display above. -->
      <template v-if="isAdmin">
        <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            class="flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700 dark:text-sky-400"
            @click="toggleExpanded(automation.id)">
            <ControlIcon :name="isExpanded(automation.id) ? 'chevron-up' : 'chevron-down'" size="0.875rem" />
            {{
              isExpanded(automation.id)
                ? tt('Hide full settings', 'Skrij vse nastavitve')
                : tt('Edit full settings', 'Uredi vse nastavitve')
            }}
          </button>
        </div>

        <div
          v-if="isExpanded(automation.id)"
          class="mt-2 rounded-2xl border border-gray-200 bg-white/80 p-3 dark:border-gray-700 dark:bg-gray-900/60">
          <FanTempControllerEditor
            v-if="automation.automationType === FAN_TEMP_CONTROLLER_TYPE"
            :model-value="draftConfig(automation.id)"
            @update:model-value="setDraftConfig(automation.id, $event)" />
          <FanPresenceTimerEditor
            v-else-if="automation.automationType === FAN_PRESENCE_TIMER_TYPE"
            :model-value="draftConfig(automation.id)"
            @update:model-value="setDraftConfig(automation.id, $event)" />
          <FanExternalControlEditor
            v-else
            :model-value="draftConfig(automation.id)"
            @update:model-value="setDraftConfig(automation.id, $event)" />
        </div>

        <!-- Save -->
        <div class="mt-4 flex justify-end">
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
            :class="isDirty(automation) ? 'bg-sky-600 hover:bg-sky-700' : 'bg-gray-300 dark:bg-gray-700'"
            :disabled="!isDirty(automation) || isSaving(automation.id) || hasErrors(automation)"
            @click="saveAutomation(automation)">
            {{ isSaving(automation.id) ? tt('Saving…', 'Shranjujem…') : tt('Save changes', 'Shrani spremembe') }}
          </button>
        </div>
      </template>
    </article>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { apiFetch } from '../../../services/api';
import { useAuthStore } from '../../../stores/auth';
import { useDeviceAutomationsStore } from '../../../stores/deviceAutomations';
import { useDeviceValues } from '../../../composables/useDeviceValues';
import {
  FAN_EXTERNAL_CONTROL_TYPE,
  FAN_PRESENCE_TIMER_TYPE,
  FAN_TEMP_CONTROLLER_TYPE,
  buildFanExternalControlDisplay,
  buildFanPresenceTimerDisplay,
  buildFanTempControllerDisplay,
  formatDurationLabel,
  getFanExternalControlErrors,
  getFanPresenceTimerErrors,
  getFanTempControllerErrors,
  normalizeFanExternalControlConfig,
  normalizeFanPresenceTimerConfig,
  normalizeFanTempControllerConfig,
} from '../../../utils/fanAutomation';
import type { DeviceAutomationSummary } from '../../../stores/deviceAutomations';
import FanTempControllerEditor from './FanTempControllerEditor.vue';
import FanPresenceTimerEditor from './FanPresenceTimerEditor.vue';
import FanExternalControlEditor from './FanExternalControlEditor.vue';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import { tt } from '../../../utils/consoleText';
import type { Bilingual } from '../../../config/bilingual';

const props = defineProps<{
  deviceId: string;
}>();

const emit = defineEmits<{
  updated: [];
}>();

const store = useDeviceAutomationsStore();
const authStore = useAuthStore();
const { currentValues } = useDeviceValues(props.deviceId);

/**
 * Editing an automation is *configuration* of the house (card `t_bd611e14`):
 * the hub serves every device-automation mutation `authenticate +
 * authorize("admin")`, so the editor and its Save button belong to an admin
 * (card `t_a4368691`; D12 — an automation schedule is configuration, so the
 * settings are not offered below that). The status display above stays: the
 * reads are `authenticate`, kept for exactly these device sheets.
 */
const isAdmin = computed(() => authStore.isAdmin);

const draftConfigs = reactive<Record<string, Record<string, unknown>>>({});
const configSignatures = reactive<Record<string, string>>({});
const savingIds = ref<string[]>([]);
const expandedIds = ref<string[]>([]);

const FAN_TYPES = [FAN_TEMP_CONTROLLER_TYPE, FAN_PRESENCE_TIMER_TYPE, FAN_EXTERNAL_CONTROL_TYPE] as const;

const summaries = computed<DeviceAutomationSummary[]>(() =>
  store
    .getDeviceAutomationSummaries(props.deviceId)
    .filter((automation) => (FAN_TYPES as readonly string[]).includes(automation.automationType)),
);

watch(
  summaries,
  (list) => {
    for (const automation of list) {
      const normalized =
        automation.automationType === FAN_TEMP_CONTROLLER_TYPE
          ? normalizeFanTempControllerConfig(automation.config)
          : automation.automationType === FAN_PRESENCE_TIMER_TYPE
            ? normalizeFanPresenceTimerConfig(automation.config)
            : normalizeFanExternalControlConfig(automation.config);
      const signature = JSON.stringify(normalized);
      if (configSignatures[automation.id] !== signature) {
        draftConfigs[automation.id] = JSON.parse(signature);
        configSignatures[automation.id] = signature;
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  void store.fetchDeviceAutomationSummaries(props.deviceId);
});

// ── Draft helpers ────────────────────────────────────────────────────────

function draftConfig(automationId: string): Record<string, unknown> {
  if (!draftConfigs[automationId]) draftConfigs[automationId] = {};
  return draftConfigs[automationId];
}

function setDraftConfig(automationId: string, next: Record<string, unknown>) {
  draftConfigs[automationId] = JSON.parse(JSON.stringify(next));
}

function isExpanded(automationId: string): boolean {
  return expandedIds.value.includes(automationId);
}

function toggleExpanded(automationId: string) {
  expandedIds.value = isExpanded(automationId)
    ? expandedIds.value.filter((id) => id !== automationId)
    : [...expandedIds.value, automationId];
}

// ── Display helpers ──────────────────────────────────────────────────────

function isActive(automation: DeviceAutomationSummary): boolean {
  if (automation.automationType === FAN_TEMP_CONTROLLER_TYPE) {
    return buildFanTempControllerDisplay(automation, currentValues.value).controllerActive;
  }
  if (automation.automationType === FAN_PRESENCE_TIMER_TYPE) {
    return buildFanPresenceTimerDisplay(automation, currentValues.value).fanActive;
  }
  return buildFanExternalControlDisplay(automation, currentValues.value).fanOn;
}

/**
 * The badge's drawn glyph (card t_a8249a54): the control sheet speaks the sheet
 * icon set, so the hub's Material names (`thermostat`, `mode_fan`, `switch` —
 * `useAutomationBadge`'s tile vocabulary) stop at the tile indicators. Each
 * type draws what it listens to: the temperature controller its thermometer,
 * the presence timer its person, the external control its switch.
 */
function automationIcon(automation: DeviceAutomationSummary): string {
  if (automation.automationType === FAN_TEMP_CONTROLLER_TYPE) {
    return 'thermometer';
  }
  if (automation.automationType === FAN_PRESENCE_TIMER_TYPE) {
    return 'person';
  }
  return 'toggle';
}

function statusLabel(automation: DeviceAutomationSummary): string {
  if (automation.enabled === false) return tt('Automation disabled', 'Avtomatizacija onemogočena');
  if (automation.automationType === FAN_TEMP_CONTROLLER_TYPE) {
    const d = buildFanTempControllerDisplay(automation, currentValues.value);
    if (d.controlSource === 'inhibited') return tt('Held by inhibit signal', 'Zadržano zaradi blokirnega signala');
    if (d.controllerActive) return tt('Regulating fan speed', 'Uravnavanje hitrosti ventilatorja');
    return tt('Waiting for sensor', 'Čakanje na senzor');
  }
  if (automation.automationType === FAN_PRESENCE_TIMER_TYPE) {
    const d = buildFanPresenceTimerDisplay(automation, currentValues.value);
    if (d.fanActive) return tt('Fan running', 'Ventilator deluje');
    if (d.timerActive) return tt('Wind-down countdown', 'Odštevanje pred izklopom');
    return tt('Monitoring presence', 'Spremljanje prisotnosti');
  }
  const d = buildFanExternalControlDisplay(automation, currentValues.value);
  if (d.fanOn) return tt('Fan on', 'Ventilator vklopljen');
  return tt('Waiting for switch', 'Čakanje na stikalo');
}

function mainValueLabel(automation: DeviceAutomationSummary): string {
  if (automation.automationType === FAN_TEMP_CONTROLLER_TYPE) {
    const d = buildFanTempControllerDisplay(automation, currentValues.value);
    return d.appliedSpeed != null ? `${d.appliedSpeed}%` : '—';
  }
  if (automation.automationType === FAN_PRESENCE_TIMER_TYPE) {
    const d = buildFanPresenceTimerDisplay(automation, currentValues.value);
    return d.targetSpeed != null ? `${d.targetSpeed}%` : '—';
  }
  const d = buildFanExternalControlDisplay(automation, currentValues.value);
  return d.lastSpeed != null ? `${d.lastSpeed}%` : '—';
}

function mainValueCaption(automation: DeviceAutomationSummary): string {
  if (automation.automationType === FAN_TEMP_CONTROLLER_TYPE) {
    const d = buildFanTempControllerDisplay(automation, currentValues.value);
    if (d.controlSource === 'pid') return tt('PID speed', 'Hitrost PID');
    if (d.controlSource === 'threshold') return tt('Speed', 'Hitrost');
    return tt('Speed', 'Hitrost');
  }
  return tt('Fan speed', 'Hitrost ventilatorja');
}

function sensorLabel(automation: DeviceAutomationSummary): string {
  const cfg = normalizeFanTempControllerConfig(automation.config);
  if (cfg.sensor_type === 'humidity') return tt('Humidity', 'Vlaga');
  if (cfg.sensor_type === 'temperature_humidity') return tt('Temp + Humidity', 'Temperatura + vlaga');
  return tt('Temperature', 'Temperatura');
}

function controlModeLabel(automation: DeviceAutomationSummary): string {
  const cfg = normalizeFanTempControllerConfig(automation.config);
  return cfg.control_mode === 'pid' ? 'PID' : tt('Threshold', 'Mejna vrednost');
}

function sensorValue(automation: DeviceAutomationSummary): string | null {
  const d = buildFanTempControllerDisplay(automation, currentValues.value);
  return d.sensorValue != null ? String(d.sensorValue) : null;
}

function presenceLabel(automation: DeviceAutomationSummary): string {
  const d = buildFanPresenceTimerDisplay(automation, currentValues.value);
  return d.presenceActive ? tt('Present', 'Prisoten') : tt('Away', 'Odsoten');
}

function timerActive(automation: DeviceAutomationSummary): boolean {
  return buildFanPresenceTimerDisplay(automation, currentValues.value).timerActive;
}

function countdownLabel(automation: DeviceAutomationSummary): string {
  const d = buildFanPresenceTimerDisplay(automation, currentValues.value);
  return formatDurationLabel(d.timeRemainingSeconds);
}

/** The switch-mode words, both languages — rendered through `tt` above. */
const SWITCH_MODE_LABELS: Record<string, Bilingual> = {
  toggle: { en: 'Toggle', sl: 'Preklop' },
  speed_cycle: { en: 'Speed cycle', sl: 'Cikel hitrosti' },
  follow: { en: 'Follow', sl: 'Sledenje' },
  momentary: { en: 'Momentary', sl: 'Momentarno' },
  speed_up_down: { en: 'Up / Down', sl: 'Gor / Dol' },
};

function switchModeLabel(automation: DeviceAutomationSummary): string {
  const cfg = normalizeFanExternalControlConfig(automation.config);
  const label = SWITCH_MODE_LABELS[cfg.switch_mode];
  return label ? tt(label.en, label.sl) : cfg.switch_mode;
}

/** The switch's own last-action words, both languages. */
const EXTERNAL_ACTION_LABELS: Record<string, Bilingual> = {
  toggle_on: { en: 'On', sl: 'Vklop' },
  toggle_off: { en: 'Off', sl: 'Izklop' },
  follow_on: { en: 'On', sl: 'Vklop' },
  follow_off: { en: 'Off', sl: 'Izklop' },
  cycle: { en: 'Speed up', sl: 'Hitreje' },
  cycle_off: { en: 'Off', sl: 'Izklop' },
  speed_up: { en: 'Speed up', sl: 'Hitreje' },
  speed_down: { en: 'Speed down', sl: 'Počasneje' },
  speed_down_off: { en: 'Off', sl: 'Izklop' },
};

function externalLastAction(automation: DeviceAutomationSummary): string | null {
  const d = buildFanExternalControlDisplay(automation, currentValues.value);
  if (!d.lastAction) return null;
  const label = EXTERNAL_ACTION_LABELS[d.lastAction];
  return label ? tt(label.en, label.sl) : d.lastAction.replace(/_/g, ' ');
}

// ── Save flow ────────────────────────────────────────────────────────────

function isDirty(automation: DeviceAutomationSummary): boolean {
  return JSON.stringify(draftConfig(automation.id)) !== (configSignatures[automation.id] ?? '');
}

function hasErrors(automation: DeviceAutomationSummary): boolean {
  if (automation.automationType === FAN_TEMP_CONTROLLER_TYPE) {
    return getFanTempControllerErrors(draftConfig(automation.id)).length > 0;
  }
  if (automation.automationType === FAN_PRESENCE_TIMER_TYPE) {
    return getFanPresenceTimerErrors(draftConfig(automation.id)).length > 0;
  }
  return getFanExternalControlErrors(draftConfig(automation.id)).length > 0;
}

function isSaving(automationId: string): boolean {
  return savingIds.value.includes(automationId);
}

async function saveAutomation(automation: DeviceAutomationSummary) {
  if (hasErrors(automation)) return;
  const automationId = automation.id;
  savingIds.value = [...savingIds.value, automationId];
  try {
    await apiFetch(`/device-automations/${automationId}`, {
      method: 'PUT',
      body: JSON.stringify({ config: draftConfig(automationId) }),
    });
    emit('updated');
    await refresh();
  } catch (error) {
    console.error(`[FanAutomationPanel] Failed to save automation ${automationId}`, error);
  } finally {
    savingIds.value = savingIds.value.filter((id) => id !== automationId);
  }
}

async function refresh() {
  store.invalidate(props.deviceId);
  await Promise.all([
    store.fetchDeviceAutomationSummaries(props.deviceId, true),
    store.fetchDeviceAutomations(props.deviceId, true),
  ]);
}
</script>
